import { PageLoader } from "@/components";
import { Header } from "@/app/modules/Navigation";
import { useMemo } from "react";
import { Favourite, ItemType } from "@/api";
import { ITEM_TYPE_COLLECTION, ITEM_TYPE_VIDEO } from "@/const.ts";
import {
  useAppProperties,
  useCollections,
  useFavourites,
  useVideos,
} from "@/hooks";
import { PlaylistGrid } from "@/app/modules/Playlists/components/PlaylistGrid.tsx";
import { cn, getTabIconFilename } from "@/utils";
import { useTranslate } from "@/providers/context";

export const Favourites = () => {
  const { data } = useFavourites();
  const { favouritesLabel, isDarkMode } = useAppProperties();
  const { translate } = useTranslate();
  const noFavourites = data?.Favourites && data.Favourites.length === 0;

  // Determine which Videos and Collections to lookup in Content API
  const { collectionIds, videoIds, items } = useMemo(() => {
    const cols: number[] = [];
    const vids: number[] = [];
    const items: { ChildId: string; Type: ItemType }[] = [];
    const favourites: Favourite[] = data?.Favourites ?? [];
    for (const f of favourites) {
      if (f.ItemType == ITEM_TYPE_VIDEO) {
        items.push({ ChildId: f.ItemId, Type: ITEM_TYPE_VIDEO });
        vids.push(parseInt(f.ItemId));
      }
      if (f.ItemType == ITEM_TYPE_COLLECTION) {
        items.push({ ChildId: f.ItemId, Type: ITEM_TYPE_COLLECTION });
        cols.push(parseInt(f.ItemId));
      }
    }

    return { collectionIds: cols, videoIds: vids, items };
  }, [data]);
  const { videoById } = useVideos({ ids: videoIds });
  const { collectionById } = useCollections({ ids: collectionIds });

  // Wait for the Favourites -> Collections + Videos to load in
  const contentNotLoaded = !videoById || !collectionById;
  if (contentNotLoaded && !noFavourites) {
    return <PageLoader />;
  }

  return (
    <div className="h-full px-7">
      <Header
        content={<div className="p1-bold">{translate(favouritesLabel)}</div>}
      />
      {contentNotLoaded || noFavourites ? (
        <div className="flex h-4/5 w-full  items-center justify-center">
          <div className="flex w-[200px] flex-col items-center text-center">
            <img
              className={cn(isDarkMode && "invert")}
              height={68}
              width={68}
              alt="favorites"
              src={getTabIconFilename({ icon: "favourites.png" })}
            />
            <div className="h4-bold  mt-4">
              {translate("No")} {translate(favouritesLabel)}
            </div>
            <div className="p1 text-item-secondary mt-2">
              {translate("Looks like you haven't saved anything yet")}
            </div>
          </div>
        </div>
      ) : (
        <PlaylistGrid
          fullItems={{ videos: videoById, collections: collectionById }}
          items={items}
        />
      )}
    </div>
  );
};
